import React from 'react';
import { useApp } from '../../context/AppContext';
import { Music, Mic2, Headphones } from 'lucide-react';

const About = () => {
  const { t, settings, language } = useApp();

  const aboutText = settings?.about_text?.[language] || `
    Music isn't just what I do – it's who I am. Every beat, every melody, every lyric comes from a place of genuine emotion and experience.

    I believe in creating music that connects, that moves people, that tells stories worth hearing. From late nights in the studio to early morning inspiration, every track is crafted with purpose and passion.

    My journey in music has been one of constant evolution, always pushing boundaries while staying true to my roots. Whether it's producing beats that make you move or writing lyrics that make you think, my goal is always the same: to create something real.
  `;

  // Use uploaded about image or fallback to default
  const aboutImage = settings?.about_image 
    ? (settings.about_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${settings.about_image}` : settings.about_image)
    : 'https://images.pexels.com/photos/18923478/pexels-photo-18923478.jpeg?auto=compress&cs=tinysrgb&w=1920';

  const heroImage = settings?.hero_image 
    ? (settings.hero_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${settings.hero_image}` : settings.hero_image)
    : 'https://images.pexels.com/photos/5749191/pexels-photo-5749191.jpeg?auto=compress&cs=tinysrgb&w=1920';

  return (
    <div className="min-h-screen bg-brand-void">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 md:px-8 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Studio"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-void via-brand-void/90 to-brand-void" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 reveal" data-testid="about-title">
            {t('about.title', 'About Me')}
          </h1>
          <p className="text-white/50 text-lg reveal" style={{ animationDelay: '0.2s' }}>
            The story behind the music
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Portrait */}
          <div className="mb-12 reveal" style={{ animationDelay: '0.3s' }}>
            <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-lg overflow-hidden">
              <img
                src={aboutImage}
                alt="Artist Portrait"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-void via-transparent to-transparent" />
            </div>
          </div>

          {/* About Text */}
          <div className="prose prose-lg prose-invert max-w-none reveal" style={{ animationDelay: '0.4s' }}>
            {aboutText.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-white/70 leading-relaxed mb-6 last:mb-0">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="p-6 bg-brand-dark rounded-lg border border-white/5 reveal" style={{ animationDelay: '0.5s' }}>
              <div className="w-12 h-12 rounded-full bg-brand-red/20 flex items-center justify-center mb-4">
                <Music className="w-6 h-6 text-brand-red" />
              </div>
              <h3 className="font-syne font-semibold text-white text-lg mb-2">Production</h3>
              <p className="text-white/50 text-sm">Creating unique sounds and beats that define the modern music landscape.</p>
            </div>

            <div className="p-6 bg-brand-dark rounded-lg border border-white/5 reveal" style={{ animationDelay: '0.6s' }}>
              <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center mb-4">
                <Mic2 className="w-6 h-6 text-brand-gold" />
              </div>
              <h3 className="font-syne font-semibold text-white text-lg mb-2">Artist</h3>
              <p className="text-white/50 text-sm">Performing and creating music that connects with audiences worldwide.</p>
            </div>

            <div className="p-6 bg-brand-dark rounded-lg border border-white/5 reveal" style={{ animationDelay: '0.7s' }}>
              <div className="w-12 h-12 rounded-full bg-brand-red/20 flex items-center justify-center mb-4">
                <Headphones className="w-6 h-6 text-brand-red" />
              </div>
              <h3 className="font-syne font-semibold text-white text-lg mb-2">Collaboration</h3>
              <p className="text-white/50 text-sm">Open for bookings, features, and creative collaborations.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
